import { types } from '@commercebear/portal-api';
import {
  anyPass,
  curry,
  groupBy,
  keys,
  map,
  pipe,
  prop,
  propEq,
  propSatisfies,
  whereEq,
  path,
  reduce,
  intersection,
  innerJoin,
  isEmpty,
  isNil,
} from 'ramda';
import Model from './Model';
import Mixable, { identifiable, paginatable } from './mixins';

const {
  shippingMethod: { LTL },
  channel: {
    OVERSTOCK_VENDOR_US,
    WAYFAIR_VENDOR_CA,
    WAYFAIR_VENDOR_US,
    HOUZZ_VENDOR_US,
  },
  orderTransition: OrderTransitions,
} = types;

export default class Order extends Mixable(Model).implementing(
  identifiable(prop('id')),
  paginatable((api) => api.orders.getOrders),
) {}

/**
 * @function
 * Determines whether the BOL for an order is one that requires manual processing by a user.
 */
export const manualBolProcess = anyPass([
  whereEq({
    channelShippingMethod: LTL,
    channel: OVERSTOCK_VENDOR_US,
  }),
  whereEq({
    channelShippingMethod: LTL,
    channel: HOUZZ_VENDOR_US,
  }),
]);

/**
 * @function
 * Determines whether cancelling an order is one that requires manual processing by a user.
 */
export const manualCancelProcess = anyPass([
  whereEq({
    channel: WAYFAIR_VENDOR_US,
  }),
  whereEq({
    channel: WAYFAIR_VENDOR_CA,
  }),
]);

/**
 * A function that extracts unique channels and their shipping method from a list of orders.
 * @param {Order[]} orders The list of orders.
 * @returns {string[]} List of unique channels and their channel shipping method.
 */
export function uniqueChannelsWithShipping(orders) {
  const groupedOrders = pipe(
    groupBy(prop('channel')),
    map(groupBy(prop('channelShippingMethod'))),
    map(keys),
  )(orders);
  const formatted = [];
  for (const [channel, channelShippingMethods] of Object.entries(groupedOrders)) {
    formatted.push(`${channel} ${channelShippingMethods.join(', ')}`);
  }
  return formatted;
}

/**
 * @typedef {Object} OrderErrors
 * @property {Order[]} ltl List of ltl orders missing tracking # or carrier.
 * @property {Order[]} smallParcel List of small parcel orders missing tracking # or carrier.
 */

/**
 * A function that returns orders that are missing tracking numbers or carriers and
 * groups them by LTL and Small Parcel shipping methods
 * @param {Order[]} orders The list of orders
 * @returns {OrderErrors} List of orders that are missing  errors
 */

export function getShippingErrors(orders) {
  const shippingCheck = anyPass([
    propSatisfies(isEmpty, 'trackingNumbers'),
    propSatisfies(isNil, 'carrier'),
  ]);

  const incompleteOrders = orders.filter(shippingCheck);

  return {
    ltl: incompleteOrders.filter(propEq('channelShippingMethod', types.shippingMethod.LTL)),
    smallParcel: incompleteOrders.filter(propEq('channelShippingMethod', types.shippingMethod.SMALL_PARCEL)),
  };
}

/**
 * A function that extracts unique channels from a list of orders.
 * @param {Order[]} orders The list of orders.
 * @returns {string[]} List of unique channels.
 */
export function uniqueChannels(orders) {
  return Object.keys(groupBy(prop('channel'))(orders));
}

export const commonWarehouses = (orderProducts) => {
  const firstWarehouses = orderProducts.length
    ? orderProducts[0].product.inventories.map(prop('warehouse'))
    : [];
  return pipe(
    map(pipe(
      path(['product', 'inventories']),
      map(path(['warehouse', 'id'])),
    )),
    reduce(intersection, firstWarehouses.map(({ id }) => id)),
    innerJoin((warehouse, id) => warehouse.id === id, firstWarehouses),
  )(orderProducts);
};

export const canPerformTransition = curry((transition, order) => order
  .transitions
  .includes(transition));

// eslint-disable-next-line max-len
export const canEditCarrier = (order) => canPerformTransition(OrderTransitions.trackingNumbers, order)
  || canPerformTransition(OrderTransitions.carrier, order)
  || canPerformTransition(OrderTransitions.GENERATE_SHIPMENT, order);
